/**
 * User Role Service
 * Quản lý mối quan hệ giữa users và roles (user_roles table)
 */

import { getSupabaseClient } from '@/utils/supabase';
import { Role, UserRole, aggregatePermissionsFromRoles } from '@/types/permission';

/**
 * Get all roles của một user
 */
export async function getUserRoles(userId: string): Promise<{
  success: boolean;
  data: Role[];
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        role:role_id (
          role_id,
          role_name,
          permissions,
          created_at,
          updated_at
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user roles:', error);
      return {
        success: false,
        data: [],
        message: error.message,
      };
    }

    // Extract roles from nested data
    const roles = data?.map((item: any) => item.role).filter(Boolean) || [];

    return {
      success: true,
      data: roles as Role[],
    };
  } catch (error: any) {
    console.error('Fatal error fetching user roles:', error);
    return {
      success: false,
      data: [],
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Get aggregated permissions from all user's roles
 */
export async function getUserPermissions(userId: string): Promise<{
  success: boolean;
  data: any[];
  message?: string;
}> {
  try {
    const rolesResult = await getUserRoles(userId);
    
    if (!rolesResult.success) {
      return {
        success: false,
        data: [],
        message: rolesResult.message,
      };
    }

    const permissions: any = aggregatePermissionsFromRoles(rolesResult.data);

    return {
      success: true,
      data: permissions,
    };
  } catch (error: any) {
    console.error('Fatal error getting user permissions:', error);
    return {
      success: false,
      data: [],
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Assign role to user
 */
export async function assignRoleToUser(
  userId: string,
  roleId: string
): Promise<{
  success: boolean;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    // Check if already assigned
    const { data: existing } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId)
      .eq('role_id', roleId)
      .single();

    if (existing) {
      return {
        success: false,
        message: 'Role already assigned to user',
      };
    }

    const { error } = await supabase
      .from('user_roles')
      .insert({
        user_id: userId,
        role_id: roleId,
      });

    if (error) {
      console.error('Error assigning role:', error);
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: true,
      message: 'Role assigned successfully',
    };
  } catch (error: any) {
    console.error('Fatal error assigning role:', error);
    return {
      success: false,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Remove role from user
 */
export async function removeRoleFromUser(
  userId: string,
  roleId: string
): Promise<{
  success: boolean;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role_id', roleId);

    if (error) {
      console.error('Error removing role:', error);
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: true,
      message: 'Role removed successfully',
    };
  } catch (error: any) {
    console.error('Fatal error removing role:', error);
    return {
      success: false,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Update user's roles (replace all roles)
 */
export async function updateUserRoles(
  userId: string,
  roleIds: string[]
): Promise<{
  success: boolean;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    // Start by removing all existing roles
    const { error: deleteError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId);

    if (deleteError) {
      console.error('Error removing existing roles:', deleteError);
      return {
        success: false,
        message: deleteError.message,
      };
    }

    // Insert new roles
    if (roleIds.length > 0) {
      const newUserRoles = roleIds.map(roleId => ({
        user_id: userId,
        role_id: roleId,
      }));

      const { error: insertError } = await supabase
        .from('user_roles')
        .insert(newUserRoles);

      if (insertError) {
        console.error('Error inserting new roles:', insertError);
        return {
          success: false,
          message: insertError.message,
        };
      }
    }

    return {
      success: true,
      message: 'User roles updated successfully',
    };
  } catch (error: any) {
    console.error('Fatal error updating user roles:', error);
    return {
      success: false,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Get all users with their roles
 */
export async function getAllUsersWithRoles(): Promise<{
  success: boolean;
  data: any[];
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    // First get all users
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (usersError) {
      console.error('Error fetching users:', usersError);
      return {
        success: false,
        data: [],
        message: usersError.message,
      };
    }

    // Lay so du vi cua tat ca user (1 query)
    const { data: wallets } = await supabase
      .from('wallet')
      .select('user_id, current_amount');
    const walletMap = new Map((wallets || []).map((w: any) => [w.user_id, Number(w.current_amount)]));

    // Then get roles for each user
    const usersWithRoles = await Promise.all(
      (users || []).map(async (user) => {
        const rolesResult = await getUserRoles(user.user_id);
        return {
          ...user,
          roles: rolesResult.success ? rolesResult.data : [],
          permissions: rolesResult.success 
            ? aggregatePermissionsFromRoles(rolesResult.data)
            : [],
          wallet_amount: walletMap.get(user.user_id) || 0,
        };
      })
    );

    return {
      success: true,
      data: usersWithRoles,
    };
  } catch (error: any) {
    console.error('Fatal error fetching users with roles:', error);
    return {
      success: false,
      data: [],
      message: error.message || 'Unknown error',
    };
  }
}

export const userRoleService = {
  getUserRoles,
  getUserPermissions,
  assignRoleToUser,
  removeRoleFromUser,
  updateUserRoles,
  getAllUsersWithRoles,
};
